import { Button, Space } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import {
  isModuleEnabled,
  usePermissions,
} from 'business/contextProviders/usePermissions';
import { DefectReportHistory } from 'business/quality-control/pages/defect-report-history/components/defect-report-table';
import { StatusTag } from 'business/quality-control/pages/defect-report-history/components/status-tag';
import { YesNoTag } from 'business/quality-control/pages/defect-report-history/components/yes-no-tag';
import { useGetDefectIndicatorData } from 'business/quality-control/pages/record-segment-defect/hooks/data/get-defect-indicator';
import { Module_Enum } from 'generated/graphql';
import { formatDate } from 'technical/format/date';

import { DefectReportStatus } from './data/types';

const ReportDateCell = ({ date, status }: { date: string; status: string }) => {
  return (
    <Space size="small">
      <span>{status === DefectReportStatus.ReportPending ? null : '✅'}</span>
      <span>{formatDate(date)}</span>
    </Space>
  );
};

const RepairDateCell = ({
  date,
  status,
}: {
  date: string | null;
  status: string;
}) => {
  if (!date) {
    return <span>-</span>;
  }

  return (
    <Space size="small">
      <span>
        {status === DefectReportStatus.RepairInProgress ? null : '✅'}
      </span>
      <span>{formatDate(date)}</span>
    </Space>
  );
};

export const useDefectReportHistoryColumns = (
  mobile: boolean,
  onOpenDrawer: (report: DefectReportHistory) => void,
) => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { isDataManager } = usePermissions();
  const { hasIndicators } = useGetDefectIndicatorData(
    currentConstructionSite.id,
  );

  const { t } = useTranslation();

  return useMemo(() => {
    let commonColumnProps: Partial<ColumnType<DefectReportHistory>> = {
      align: 'center',
    };

    if (mobile) {
      commonColumnProps = {
        ...commonColumnProps,
        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
      };
    }

    return [
      ...(isModuleEnabled(
        isDataManager,
        currentConstructionSite,
        Module_Enum.QualityScan,
      )
        ? [
            {
              title: t('qualityControl.defectReportHistory.serialNumber'),
              dataIndex: 'serialNumber',
              key: 'serialNumber',
              width: mobile ? 100 : 60,
              sorter: true,
              render: (serialNumber: string) => (
                <span>{serialNumber ? serialNumber : '-'}</span>
              ),
              ...commonColumnProps,
            } satisfies ColumnType<DefectReportHistory>,
          ]
        : []),
      {
        title: t('qualityControl.defectReportHistory.segmentType'),
        dataIndex: 'segmentType',
        key: 'segmentType',
        width: mobile ? 100 : 60,
        sorter: true,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.defectReportHistory.defectType'),
        dataIndex: 'defectType',
        key: 'defectType',
        width: mobile ? 140 : 120,
        sorter: true,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.defectReportHistory.toRepair'),
        dataIndex: 'toRepair',
        key: 'toRepair',
        width: mobile ? 90 : 75,
        sorter: true,
        render: (toRepair: boolean) => <YesNoTag value={toRepair} />,
        ...commonColumnProps,
      },
      ...(hasIndicators
        ? [
            {
              title: t('qualityControl.defectReportHistory.indicator'),
              dataIndex: 'indicator',
              key: 'indicator',
              width: mobile ? 140 : 120,
              sorter: true,
              render: (indicator: string | null) => (
                <span>{indicator ?? '-'}</span>
              ),
              ...commonColumnProps,
            } satisfies ColumnType<DefectReportHistory>,
          ]
        : []),
      {
        title: t('qualityControl.defectReportHistory.reportDate'),
        dataIndex: 'reportDate',
        key: 'reportDate',
        width: mobile ? 125 : 100,
        sorter: true,
        render: (date: string, record: DefectReportHistory) => {
          return <ReportDateCell date={date} status={record.status} />;
        },
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.defectReportHistory.repairDate'),
        dataIndex: 'repairDate',
        key: 'repairDate',
        width: mobile ? 125 : 100,
        sorter: true,
        render: (date: string, record: DefectReportHistory) => {
          return <RepairDateCell date={date} status={record.status} />;
        },
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.defectReportHistory.statusSearchable.title'),
        dataIndex: 'status',
        key: 'status',
        width: 75,
        sorter: true,
        render: (status: string) => <StatusTag status={status} />,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.defectReportHistory.ring'),
        dataIndex: 'ringNumber',
        key: 'ringNumber',
        width: mobile ? 90 : 75,
        sorter: true,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.defectReportHistory.actions.header'),
        key: 'actions',
        render: (_, record) => {
          return (
            <Button
              color="default"
              variant="filled"
              onClick={() => onOpenDrawer(record)}
            >
              {t('qualityControl.defectReportHistory.actions.openDetails')}
            </Button>
          );
        },
        width: 50,
        ...commonColumnProps,
      },
    ] satisfies ColumnType<DefectReportHistory>[];
  }, [
    t,
    onOpenDrawer,
    mobile,
    currentConstructionSite,
    isDataManager,
    hasIndicators,
  ]);
};
